export interface ConsentObject {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
  dateAccepted: string;
  consentVersion: string;
}

export type CookieCategory = keyof Omit<ConsentObject, 'dateAccepted' | 'consentVersion'>;

const COOKIE_NAME = 'wallibou_cookie_consent';
const CONSENT_VERSION = '1.0';
const MAX_AGE_SECONDS = 15552000; // ~6 months

export const getCookieValue = (name: string): string => {
  const prefix = `${name}=`;
  const cookie = document.cookie.split(';').find((item) => item.trim().indexOf(prefix) === 0);
  return cookie ? cookie.trim().slice(prefix.length) : '';
};

export const makeConsent = (
  analytics: boolean,
  marketing: boolean,
  functional: boolean,
): ConsentObject => ({
  essential: true,
  analytics: Boolean(analytics),
  marketing: Boolean(marketing),
  functional: Boolean(functional),
  dateAccepted: new Date().toISOString(),
  consentVersion: CONSENT_VERSION,
});

export const readConsent = (): ConsentObject | null => {
  const value = getCookieValue(COOKIE_NAME);
  if (!value) return null;

  if (value === 'accepted') return makeConsent(true, true, true);

  try {
    const decoded = decodeURIComponent(value);
    const consent = JSON.parse(decoded) as ConsentObject;

    if (consent.essential !== true) return null;
    if (consent.consentVersion !== CONSENT_VERSION) return null;

    return consent;
  } catch (error) {
    console.error('[Cookie Consent] Parse error:', error);
    return null;
  }
};

export const writeConsent = (consent: ConsentObject): void => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const cookieString = `${COOKIE_NAME}=${encodeURIComponent(
    JSON.stringify(consent),
  )}; Path=/; Max-Age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  document.cookie = cookieString;
};

export const getConsentModeState = (consent: ConsentObject | null): Record<string, string> => ({
  ad_storage: consent?.marketing ? 'granted' : 'denied',
  ad_user_data: consent?.marketing ? 'granted' : 'denied',
  ad_personalization: consent?.marketing ? 'granted' : 'denied',
  analytics_storage: consent?.analytics ? 'granted' : 'denied',
  functionality_storage: consent?.functional ? 'granted' : 'denied',
  personalization_storage: consent?.functional ? 'granted' : 'denied',
  security_storage: 'granted',
});
